import useDateHelperUtils from 'src/composables/dateHelperUtils';
import useRateHelperUtils from 'src/composables/rateHelperUtils.ts';
import useUtils from 'src/composables/utils';
import {
  CreditCardDTO,
  PolicyDTO,
  ApiDropdown,
  ManageBookingConfigurationDTO,
  SearchReservationDTO,
  AccountDTO,
  UpdateReservationRequestDTO,
  ReservationItemViewDTO,
  ReservationItemEditDTO,
  RateDTO,
  RoomTypeDTO,
  ReservationItemRateRowDTO,
  RoomRatePromotionViewDTO,
  VisitorLevyDTO,
} from 'src/services/api/data-contracts';
import {
  ReservationItemService,
  Guest,
  Booker,
  IApiDropdownOption,
  IReservationItem,
  RoomTypeIdQuantityChecker,
  IVatSettings,
  IReservationItemUpgrade,
  Rate,
} from 'src/types/common';

const dateHelperUtils = useDateHelperUtils();
const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();

export enum ManageBookingProcessStep {
  None = 0,
  Start = 1,
  ReservationOverview = 2,
  Booker = 3,
  Room = 4,
  Guest = 5,
  Extras = 6,
  RoomTypeSelection = 7,
  RatePlanSelection = 8,
  UpgradeSelection = 9,
  ChangeDates = 10,
  Summary = 11,
  Complete = 12,
  Cancelled = 13,
}

export class ManageBookingReservation {
  constructor(
    data: SearchReservationDTO,
    config: ManageBookingConfigurationDTO,
    roomTypeQuantityChecker: RoomTypeIdQuantityChecker,
    vatSettings: IVatSettings,
  ) {
    this.PropertyId = data.PropertyId;
    this.ReservationId = data.ReservationId;
    this.ReservationNumber = data.ReservationNumber;

    this.DateFrom = data.DateFrom;
    this.DateTo = data.DateTo;

    this.SpecialRequests = data.SpecialRequests;

    this.CanBeEdited = data.CanBeEdited;
    this.CanBeCancelled = data.CanBeCancelled;
    this.IsCancelled = data.IsCancelled;

    this.CreditCards = data.CreditCards;
    this.Booker =
      data.Booker != null
        ? new Booker(data.Booker, config.BookerConfiguration)
        : null;
    this.Company = data.Company;

    this.ReservationItems = data.Rooms.map((g, index) => {
      const ri = new ManageBookingReservationItem(
        g,
        config,
        roomTypeQuantityChecker,
      );
      ri.VatSettings = vatSettings;
      ri.roomNumber = index + 1;
      return ri;
    });
    this.ReservationItemsToCancel = [];

    this.OriginalTotalAccommodationPrice = data.TotalAccommodationPrice;
    this.OriginalTotalServicesPrice = data.TotalServicesPrice;

    this.visitorLevy = data.VisitorLevy;
  }

  PropertyId: number;
  ReservationId: number;
  ReservationNumber: string;
  CanBeEdited: boolean;
  CanBeCancelled: boolean;
  IsCancelled: boolean;

  DateFrom: string;
  DateTo: string;

  visitorLevy: VisitorLevyDTO | null;

  get DateFromDisplay(): string {
    const dateDisplay = dateHelperUtils.formatDateDisplay(
      this.DateFrom,
      'dddd, DD MMMM YYYY',
    );
    return dateDisplay;
  }

  get DateToDisplay(): string {
    const dateDisplay = dateHelperUtils.formatDateDisplay(
      this.DateTo,
      'dddd, DD MMMM YYYY',
    );
    return dateDisplay;
  }

  get numberOfNights(): number {
    const totalDays = dateHelperUtils.calculateNumberOfNights(
      this.DateFrom,
      this.DateTo,
    );
    return totalDays;
  }

  SpecialRequests: string;
  OriginalTotalAccommodationPrice: number;
  OriginalTotalServicesPrice: number;

  get NumberOfGuests(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.NumberOfAdults + y.NumberOfChildren + y.NumberOfInfants;
    }, 0);
  }

  get NumberOfRooms(): number {
    return this.ReservationItems.length;
  }

  get TotalAccommodationPrice(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.TotalAccommodationPrice;
    }, 0);
  }

  get TotalServicesPrice(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.TotalServicesPrice;
    }, 0);
  }

  get TotalVatAmount(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.TotalAccommodationVat + y.TotalServicesVat;
    }, 0);
  }

  CreditCards: CreditCardDTO[];
  Booker?: Booker;
  Company?: AccountDTO;
  ReservationItems: ManageBookingReservationItem[];
  ReservationItemsToCancel: RoomToCancel[];

  get welcomeName(): string {
    const b = this.Booker;
    if (b != null) {
      return b.FirstName;
    } else {
      return 'Guest';
    }
  }

  get leadGuestFullName(): string {
    const b = this.Booker;
    if (b != null) {
      return b.FirstName + ' ' + b.LastName;
    } else if (this.Company != null) {
      return this.Company.Name;
    }
  }

  cancelRoom(
    item: ManageBookingReservationItem,
    cancellationReason: IApiDropdownOption,
  ) {
    if (item.ReservationItemId != null) {
      const c = new RoomToCancel();
      c.ReservationItemId = item.ReservationItemId;
      c.CancellationReason = cancellationReason;
      this.ReservationItemsToCancel.push(c);
    }

    const index = this.ReservationItems.indexOf(item);
    if (index > -1) {
      this.ReservationItems.splice(index, 1);
    }
  }

  updateGuestsWithBooker() {
    const bookerGuests = this.ReservationItems.flatMap(
      (reservation) => reservation.Guests,
    ).filter((guest) => guest.IndividualId === this.Booker.IndividualId);

    if (bookerGuests.length > 0) {
      bookerGuests.forEach((bg) => {
        bg.FirstName = this.Booker.FirstName;
        bg.LastName = this.Booker.LastName;
        bg.Email = this.Booker.Email;
        bg.Phone = this.Booker.Phone;

        bg.haveReviewedGuest = true;
      });
    }
  }

  toDTO(): UpdateReservationRequestDTO {
    return {
      PropertyId: this.PropertyId,
      ReservationId: this.ReservationId,
      SpecialRequests: this.SpecialRequests,
      Booker: this.Booker != null ? this.Booker.toDTO() : null,
      RoomsToCancel: this.ReservationItemsToCancel.map((ri) => {
        return ri.toDTO();
      }),
      ReservationItems: this.ReservationItems.map((ri) => {
        return ri.toDTO();
      }),
    };
  }
}

export class ManageBookingReservationItem implements IReservationItem {
  constructor(
    data: ReservationItemViewDTO,
    config: ManageBookingConfigurationDTO,
    roomTypeQuantityChecker: RoomTypeIdQuantityChecker,
  ) {
    this.ReservationItemId = data.ReservationItemId;
    this.roomId = this.ReservationItemId?.toString();
    this.roomNumber = 0;

    this.roomTypeQuantityChecker = roomTypeQuantityChecker;

    this.CanBeCancelled = data.CanBeCancelled;
    this.CanBeEdited = data.CanBeEdited;

    this.DateOfArrival = data.DateOfArrival;
    this.DateOfDeparture = data.DateOfDeparture;

    this.RoomTypeId = data.RoomTypeId;
    this.RatePlanId = data.RatePlanId;
    this.RoomType = data.RoomTypeName;
    this.RatePlan = data.RatePlanName;

    this.Policy = data.Policy;

    this.RoomConfiguration = data.RoomConfiguration;
    this.RoomConfigurationIsRequired = config.ShowRoomConfigurationQuestion;

    this.NumberOfAdults = data.NumberOfAdults;
    this.NumberOfChildren = data.NumberOfChildren;
    this.NumberOfInfants = data.NumberOfInfants;

    this.OriginalTotalAccommodationPrice = data.TotalAccommodationPrice;
    this.OriginalTotalServicesPrice = data.TotalServicesPrice;
    this.OriginalTotalAccommodationVat = data.TotalAccommodationVat;
    this.OriginalTotalServicesVat = data.TotalServicesVat;

    this.Guests = data.Guests.map((g) => {
      return new Guest(g, config.GuestConfiguration);
    });
    this.Services = data.Services.map((g) => {
      return new ReservationItemService(g);
    });

    this.haveReviewedRoom = false;

    this.haveSeenServices = false;
    this.areServicesLoaded = false;
    this.hasSelectableServices = false;

    this.haveSeenUpgrades = false;
    this.availableUpgrades = [];
  }

  roomTypeQuantityChecker: RoomTypeIdQuantityChecker;

  roomId: string;
  roomNumber: number;

  ReservationItemId: number;

  haveReviewedRoom: boolean;
  get haveReviewedAllGuests(): boolean {
    return this.Guests.reduce((t, y) => {
      return t && y.haveReviewedGuest && y.isValid;
    }, true);
  }

  DateOfArrival: string;
  DateOfDeparture: string;
  RoomTypeId: number;
  RoomType: string;
  roomType: RoomTypeDTO;

  RatePlanId: number;
  RatePlan: string;
  Rate: RateDTO; // Only used when selecting a new room to upgrade.

  updatingRates: boolean;
  visitorLevy: number;

  get RoomTypeDisplay(): string {
    return this.RoomType;
  }
  get RatePlanDisplay(): string {
    return this.RatePlan;
  }
  get GuestSummaryDisplay(): string {
    return this.guestSummary;
  }

  Policy?: PolicyDTO;
  CanBeCancelled: boolean;
  CanBeEdited: boolean;

  RoomConfigurationIsRequired: boolean;
  RoomConfiguration?: ApiDropdown;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  CreditCards: CreditCardDTO[];
  Guests: Guest[];

  get guestSummary(): string {
    return utils.getGuestSummaryText(
      this.NumberOfAdults,
      this.NumberOfChildren,
      this.NumberOfInfants,
    );
  }

  get ChildrenAndInfants(): string {
    return utils.getGuestSummaryText(
      0,
      this.NumberOfChildren,
      this.NumberOfInfants,
    );
  }

  get numberOfNights(): number {
    const totalDays = dateHelperUtils.calculateNumberOfNights(
      this.DateOfArrival,
      this.DateOfDeparture,
    );
    return totalDays;
  }

  get numberOfGuests(): number {
    return this.NumberOfAdults + this.NumberOfChildren;
  }

  get isValid(): boolean {
    return (
      this.guestsAreValid &&
      (!this.RoomConfigurationIsRequired ||
        (this.RoomConfigurationIsRequired && this.RoomConfiguration != null))
    );
  }

  get roomConfigurationIsValid(): boolean {
    return (
      !this.RoomConfigurationIsRequired ||
      (this.RoomConfigurationIsRequired && this.RoomConfiguration != null)
    );
  }

  get guestsAreValid(): boolean {
    const v = this.Guests.reduce((v, g) => {
      return v && g.isValid;
    }, true);
    return v;
  }

  activeGuestNumber(guest: Guest): number {
    let index = -2;

    index = this.Guests.findIndex((u) => u == guest);
    index = index + 1;

    return index;
  }

  /// Find a reservation item based on Room Id, with an optional selector
  getGuest(
    guestId: string,
    selector: (ri: Guest) => boolean = () => {
      return true;
    },
  ): Guest | null {
    const items = this.Guests.filter((y) => {
      return y.guestId == guestId && selector(y);
    });
    if (items.length > 0) {
      return items[0];
    }
    return null;
  }

  getNextGuest(
    currentGuest: Guest,
    selector: (ri: Guest) => boolean,
  ): Guest | null {
    let nextGuest: Guest = null;
    let index = -1;

    const getNextGuest = (index: number) => {
      let nextX = this.Guests[index];
      if (nextX != null) {
        const resX = this.getGuest(nextX.guestId, selector);
        if (resX == null) {
          nextX = getNextGuest(index + 1);
        } else {
          nextX = resX;
        }
      }

      return nextX;
    };

    if (currentGuest == null) {
      index = 0;
      nextGuest = getNextGuest(index);
    } else {
      index = this.Guests.findIndex((y) => {
        return y.guestId == currentGuest?.guestId;
      });

      if (index > -1) {
        nextGuest = getNextGuest(index + 1);
      }
    }

    return nextGuest;
  }

  // True if they have seen the upgrades for this RI.
  haveSeenUpgrades: boolean;
  availableUpgrades: IReservationItemUpgrade[];

  get hasUpgrades(): boolean {
    const upgrades = this.availableUpgrades;
    return upgrades.length > 0;
  }

  clearUpgrades() {
    this.availableUpgrades.splice(0); // maintain reactivity.
  }

  // check if there are upgrades for this rate to any room type.
  checkForUpgrades() {
    const rpId = this.RatePlanId;

    if (this.roomTypeQuantityChecker == null) {
      this.roomTypeQuantityChecker = () => {
        return 0;
      };
    }

    if (this.roomType != null && this.roomType.Upgrades != null) {
      const ratePlanUpgrades = this.roomType.Upgrades.map((rtUpgrade) => {
        const availableQuantity = this.roomTypeQuantityChecker(
          rtUpgrade.RoomTypeId,
        );

        if (availableQuantity <= 0) {
          return null;
        }

        const matchingUpgradeRateDTO = rtUpgrade.AvailableRates.find((ar) => {
          return ar.RatePlanId == rpId;
        });
        if (matchingUpgradeRateDTO == null) {
          return null;
        } else {
          const utils = useUtils();

          const matchingUpgradeRate = new Rate(matchingUpgradeRateDTO);

          const isRoomTypeOccupancyValid = utils.checkIfOccupancyIsValid(
            this.NumberOfAdults,
            this.NumberOfChildren,
            this.NumberOfInfants,
            rtUpgrade.MaximumOccupancy,
            rtUpgrade.MinimumOccupancy,
            rtUpgrade.AdultMaximumOccupancy,
            rtUpgrade.ChildMaximumOccupancy,
          );

          let ratePlanOccupancyIsValid = true;
          if (matchingUpgradeRate.UseOccupancyForRatePlan) {
            ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
              this.NumberOfAdults,
              this.NumberOfChildren,
              this.NumberOfInfants,
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.MaximumOccupancy,
                rtUpgrade.MaximumOccupancy,
              ),
              utils.getLargestMinimumValue(
                matchingUpgradeRate.MinimumOccupancy,
                rtUpgrade.MinimumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.AdultMaximumOccupancy,
                rtUpgrade.AdultMaximumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.ChildMaximumOccupancy,
                rtUpgrade.ChildMaximumOccupancy,
              ),
            );
          }

          if (isRoomTypeOccupancyValid && ratePlanOccupancyIsValid) {
            let cost = 0;
            const rateHelperUtils = useRateHelperUtils();
            cost = rateHelperUtils.calculateRoomRate(
              this.NumberOfAdults,
              this.NumberOfChildren,
              this.NumberOfInfants,
              matchingUpgradeRate.BaseRate,
              matchingUpgradeRate.BaseNumberOfAdults,
              matchingUpgradeRate.CostPerRoom,
              matchingUpgradeRate.CostPerAdult,
              matchingUpgradeRate.CostPerChild,
              matchingUpgradeRate.CostPerInfant,
              matchingUpgradeRate.PerPersonCostPerAdult,
              matchingUpgradeRate.PerPersonCostPerChild,
              matchingUpgradeRate.PerPersonCostPerInfant,
            );

            return {
              roomType: rtUpgrade,
              rate: matchingUpgradeRate,
              totalUpgradeCost: cost,
              totalCostDifference: cost - this.TotalAccommodationPrice,
            } as IReservationItemUpgrade;
          } else {
            return null;
          }
        }
      });
      this.availableUpgrades = ratePlanUpgrades.filter((ug) => {
        return ug != null;
      });

      if (!this.hasUpgrades) {
        this.haveSeenUpgrades = true;
      }
    }
  }

  Services: ReservationItemService[];

  // True if there are services available for selection
  hasSelectableServices: boolean;
  areServicesLoaded: boolean;
  haveSeenServices: boolean;

  get hasServices(): boolean {
    return this.Services.length > 0;
  }

  get canEditServices(): boolean {
    let canEdit = false;
    if (this.Services.length == 0) {
      canEdit = true;
    } else {
      canEdit = this.Services.reduce((v, ris) => {
        return v || ris.canEdit || ris.justAdded;
      }, false);
    }

    return canEdit;
  }

  get canShowEditServices(): boolean {
    return (
      this.hasServices || (this.hasSelectableServices && this.canEditServices)
    );
  }

  getService(serviceId: number): ReservationItemService | null {
    const s = this.Services.filter((u) => {
      return u.ServiceId == serviceId;
    });

    return s.length > 0 ? s[0] : null;
  }

  OriginalTotalAccommodationPrice: number;
  OriginalTotalServicesPrice: number;
  OriginalTotalAccommodationVat: number;
  OriginalTotalServicesVat: number;

  VatSettings: IVatSettings;

  get TotalServicesPrice(): number {
    const cost = rateHelperUtils.calcuateTotalServiceCost(
      this.Services,
      this.numberOfNights,
      this.numberOfGuests,
    );
    return cost;
  }

  get TotalServicesVat(): number {
    const vatAmount = rateHelperUtils.calculateVatAmount(
      this.TotalServicesPrice,
      this.VatSettings,
    );
    return vatAmount;
  }

  get TotalAccommodationPrice(): number {
    let cost = this.OriginalTotalAccommodationPrice;
    if (this.Rate != null) {
      const rate = new Rate(this.Rate);

      cost = rateHelperUtils.calculateRoomRate(
        this.NumberOfAdults,
        this.NumberOfChildren,
        this.NumberOfInfants,
        rate.BaseRate,
        rate.BaseNumberOfAdults,
        rate.CostPerRoom,
        rate.CostPerAdult,
        rate.CostPerChild,
        rate.CostPerInfant,
        rate.PerPersonCostPerAdult,
        rate.PerPersonCostPerChild,
        rate.PerPersonCostPerInfant,
      );
    }
    return cost;
  }

  get TotalAccommodationVat(): number {
    let vat = this.OriginalTotalAccommodationVat;
    if (this.Rate != null) {
      vat = rateHelperUtils.calculateVatAmount(
        this.TotalAccommodationPrice,
        this.VatSettings,
      );
    }
    return vat;
  }

  toDTO(): ReservationItemEditDTO {
    return {
      ReservationItemId: this.ReservationItemId,
      DateOfArrival: this.DateOfArrival,
      DateOfDeparture: this.DateOfDeparture,
      NumberOfAdults: this.NumberOfAdults,
      NumberOfChildren: this.NumberOfChildren,
      NumberOfInfants: this.NumberOfInfants,

      RatePlanId: this.RatePlanId,
      RoomTypeId: this.RoomTypeId,

      RoomConfigurationDropdownOptionId: this.RoomConfiguration?.Id,

      Guests: this.Guests.map((g) => {
        return g.toDTO();
      }),

      Services: this.Services.map((ris: ReservationItemService) => {
        return {
          ReservationItemId: ris.ReservationItemId,
          ServiceId: ris.ServiceId,
          Quantity: ris.Quantity,
          Service: ris.Service,
        } as ReservationItemService;
      }),

      TotalCost: this.TotalAccommodationPrice,
    };
  }
}

export class ManageBookingDateAvailabilityRow
  implements ReservationItemRateRowDTO
{
  constructor(data: ReservationItemRateRowDTO) {
    Object.assign(this, data);
  }

  get totalCost(): number {
    let cost = 0;
    const rateHelperUtils = useRateHelperUtils();
    cost = rateHelperUtils.calculateRoomRate(
      this.NumberOfAdults,
      this.NumberOfChildren,
      this.NumberOfInfants,
      this.BaseRate,
      this.BaseNumberOfAdults,
      this.CostPerRoom,
      this.CostPerAdult,
      this.CostPerChild,
      this.CostPerInfant,
      this.PerPersonCostPerAdult,
      this.PerPersonCostPerChild,
      this.PerPersonCostPerInfant,
    );
    return cost;
  }

  /** @format int32 */
  ReservationItemId?: number;
  /** @format date-time */
  DateFrom?: string;
  /** @format date-time */
  DateTo?: string;
  IsOccupancyBased?: boolean;
  /** @format double */
  BaseRate?: number;
  /** @format int32 */
  BaseNumberOfAdults?: number;
  /** @format double */
  CostPerRoom?: number;
  /** @format double */
  CostPerAdult?: number;
  /** @format double */
  CostPerChild?: number;
  /** @format double */
  CostPerInfant?: number;
  /** @format double */
  OriginalBaseRate?: number;
  /** @format double */
  OriginalCostPerRoom?: number;
  /** @format double */
  OriginalCostPerAdult?: number;
  /** @format double */
  OriginalCostPerChild?: number;
  /** @format double */
  OriginalCostPerInfant?: number;
  /** @format double */
  PerPersonCostPerAdult?: number;
  /** @format double */
  PerPersonCostPerChild?: number;
  /** @format double */
  PerPersonCostPerInfant?: number;
  /** @format double */
  OriginalPerPersonCostPerAdult?: number;
  /** @format double */
  OriginalPerPersonCostPerChild?: number;
  /** @format double */
  OriginalPerPersonCostPerInfant?: number;
  /** @format int32 */
  NumberOfAdults?: number;
  /** @format int32 */
  NumberOfChildren?: number;
  /** @format int32 */
  NumberOfInfants?: number;
  Promotion?: RoomRatePromotionViewDTO;
}

export class RoomToCancel {
  constructor() {}
  ReservationItemId: number;
  CancellationReason: IApiDropdownOption;

  toDTO() {
    return {
      ReservationItemId: this.ReservationItemId,
      CancellationReasonId: this.CancellationReason?.Id,
    };
  }
}
